import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatDynamicTableComponentConfig} from './configs/mat-dynamic-table-component.config';
import {MatPaginator, MatSort, MatTableDataSource} from '@angular/material';
import {FormControl} from '@angular/forms';
import {ApiCallModel} from '../shared/models/api-call.model';
import {Observable, Subject} from 'rxjs';
import {MatDynamicTableService} from './mat-dynamic-table.service';
import {debounceTime, filter, map, takeUntil} from 'rxjs/operators';
import {distinctUntilChanged} from 'rxjs/internal/operators/distinctUntilChanged';
import {startWith} from 'rxjs/internal/operators/startWith';
import {combineLatest} from 'rxjs/internal/observable/combineLatest';

import * as _ from 'lodash';

@Component({
  selector: 'bd-mat-dynamic-table',
  templateUrl: './mat-dynamic-table.component.html',
  styleUrls: ['./mat-dynamic-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class MatDynamicTableComponent implements OnChanges, OnInit, OnDestroy {

  @Input() data: any[];
  @Input() componentConfig: MatDynamicTableComponentConfig;

  dataSource: MatTableDataSource<any>;
  expandedElement: any;

  searchCtrl: FormControl = new FormControl();
  @ViewChild(MatPaginator) matPaginator: MatPaginator;
  @ViewChild(MatSort) matSort: MatSort;

  previousSearch: string;

  @Output() details: EventEmitter<any> = new EventEmitter();
  @Output() edit: EventEmitter<any> = new EventEmitter();
  @Output() delete: EventEmitter<any> = new EventEmitter();
  @Output() download: EventEmitter<any> = new EventEmitter();
  @Output() apiCall: EventEmitter<ApiCallModel> = new EventEmitter();

  private _onDestroy: Subject<void> = new Subject();

  constructor(public service: MatDynamicTableService) {
  }

  ngOnChanges() {
    this.dataSource = new MatTableDataSource(this.service.getParsedData(this.data, this.componentConfig.columns));
  }

  ngOnInit() {
    this.matPaginator.pageIndex = 0;
    this.matPaginator.pageSize = this.componentConfig.paginator.pageSize;
    this.matPaginator.pageSizeOptions = this.componentConfig.paginator.pageSizeOptions;

    if (this.componentConfig.apiable) {
      this.matPaginator.length = this.componentConfig.paginator.length;

      const observables: Array<Observable<any>> = [
        this.searchCtrl.valueChanges
          .pipe(debounceTime(1000),
            distinctUntilChanged(),
            startWith(undefined)),
        this.matPaginator.page
          .pipe(startWith(undefined)),
        this.matSort.sortChange
          .pipe(startWith(undefined))
      ];

      combineLatest(observables)
        .pipe(takeUntil(this._onDestroy),
          filter(value => !_.isEqual(value, [undefined, undefined, undefined])),
          map(value => {
            if (this.previousSearch && value[0] !== this.previousSearch) {
              this.matPaginator.pageIndex = 0;
              value[1].previousPageIndex = value[1].pageIndex;
              value[1].pageIndex = 0;
            }

            if (!value[1]) {
              value[1] = {
                pageIndex: 0,
                pageSize: this.componentConfig.paginator.pageSize,
                length: this.componentConfig.paginator.length
              };
            }

            if (value[2]) {
              value[2]['direction'] = (<string>value[2]['direction']).toUpperCase();
            }

            this.previousSearch = value[0];

            return value;
          })).subscribe((value) => {
        this.apiCall.emit({
          search: value[0],
          paginator: value[1],
          sort: value[2]
        });
      });
    } else {
      this.dataSource.paginator = this.matPaginator;
      this.dataSource.sort = this.matSort;
      this.searchCtrl.valueChanges
        .pipe(debounceTime(1000),
          takeUntil(this._onDestroy))
        .subscribe(value => {
          this.dataSource.filter = value.trim().toLowerCase();
          if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onDetailsClick(index: number) {
    this.details.emit(this.data[index]);
  }

  onEditClick(index: number) {
    this.edit.emit(this.data[index]);
  }

  onDeleteClick(index: number) {
    this.delete.emit(this.data[index]);
  }

  onDownloadClick(index: number) {
    this.download.emit(this.data[index]);
  }
}
